import { useEffect, useMemo, useState } from 'react';

import { AircraftData } from '#entities';

import {
  findCaseForReference,
  getAircraftInfo,
  parseAircraftEvents,
  parseDonorReference,
} from '../helpers';

import { useData } from './contexts';

export const useAircrafts = () => {
  const { newCases, activeCases, aircraftsRawList } = useData();
  const [aircrafts, setAircrafts] = useState<AircraftData[]>([]);

  useEffect(() => {
    const fetchAircrafts = async () => {
      if (!aircraftsRawList?.length) {
        setAircrafts([]);
        return;
      }

      const resolved = await Promise.all(aircraftsRawList.map(getAircraftInfo));
      setAircrafts(resolved);
    };

    fetchAircrafts();
  }, [aircraftsRawList]);

  return useMemo(
    () =>
      aircrafts.map(aircraft => {
        const allParsed = parseDonorReference(
          aircraft.tripActual?.ExternalReference,
        );
        const primary = allParsed[0];
        const { unosId, organShort } = primary;
        if (!unosId || unosId.length < 7) {
          return aircraft;
        }

        // Prefer the primary (first) organ in the External Reference so the
        // aircraft color/label follows the first case, not whichever matching
        // case happens to be first in the array (TSMED-1396).
        const match = findCaseForReference(activeCases, allParsed);

        const newCaseMatch = findCaseForReference(newCases, allParsed);

        const actualLabel =
          match?.label ||
          newCaseMatch?.label ||
          aircraft.tripActual?.ExternalReference;

        const onTrialCase = Boolean(match?.trialProgram);

        const aircraftEvents = parseAircraftEvents(
          aircraft.aircraftEvents,
          unosId,
          organShort,
          onTrialCase,
        );

        if (
          aircraft.actualLabel === actualLabel &&
          aircraft.onTrialCase === onTrialCase &&
          aircraft.aircraftEvents === aircraftEvents
        ) {
          return aircraft;
        }
        return { ...aircraft, actualLabel, onTrialCase, aircraftEvents };
      }),
    [aircrafts, activeCases, newCases],
  );
};
